import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PhoneIcon, MailIcon, LocationIcon, ClockIcon, SendIcon } from './icons/ModernIcons';

const Contact = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create mailto link with form data
    const subject = encodeURIComponent('Anfrage über Website');
    const body = encodeURIComponent(`
Name: ${formData.name}
E-Mail: ${formData.email}

Nachricht:
${formData.message}
    `);
    window.location.href = `mailto:info@lima-service.de?subject=${subject}&body=${body}`;
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: 'Telefon',
      info: '01755525889',
      link: 'tel:01755525889',
      color: 'blue'
    },
    {
      icon: MailIcon,
      title: 'E-Mail',
      info: 'info@lima-service.de',
      link: 'mailto:info@lima-service.de',
      color: 'purple'
    },
    {
      icon: LocationIcon,
      title: 'Adresse',
      info: 'Röttenbacher Straße 5\n91785 Pleinfeld\nDeutschland',
      color: 'green'
    },
    {
      icon: ClockIcon,
      title: 'Öffnungszeiten',
      info: 'Mo-Fr: 9:00-18:00\nSa: 9:00-14:00\nSo: Nach Vereinbarung',
      color: 'orange'
    }
  ];

  return (
    <section id="contact" className="py-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
          alt="Kontakt Hintergrund"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-blue-900/90 to-purple-900/95"></div>
      </div>

      {/* Floating elements */}
      <div className="absolute inset-0">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full"
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              delay: i * 0.5,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Kontaktieren Sie{' '}
            <span className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
              uns
            </span>
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Haben Sie Fragen oder möchten Sie ein unverbindliches Angebot? Wir freuen uns auf Ihre Nachricht!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Sprechen Sie uns an
              </h3>
              <p className="text-blue-100 mb-8">
                Unser Team berät Sie gerne persönlich zu Ihrem Textildruck-Projekt. Kontaktieren Sie uns für eine individuelle Beratung und ein unverbindliches Angebot.
              </p>
            </div>

            {/* Contact info cards */}
            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-lg border border-white/20 hover:bg-white/20 transition-all"
                >
                  <div className={`w-12 h-12 bg-gradient-to-r from-${item.color}-400 to-${item.color}-500 rounded-lg flex items-center justify-center mb-4`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold text-white mb-2">{item.title}</h4>
                  {item.link ? (
                    <a
                      href={item.link}
                      className={`text-${item.color}-300 hover:text-${item.color}-200 transition-colors text-sm`}
                    >
                      {item.info}
                    </a>
                  ) : (
                    <p className="text-blue-200 text-sm whitespace-pre-line">{item.info}</p>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Location image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="relative rounded-xl overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Pleinfeld Fränkisches Seenland"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <h4 className="font-bold">Pleinfeld</h4>
                <p className="text-sm text-gray-200">Fränkisches Seenland</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-xl p-6 text-white border border-white/20"
            >
              <h4 className="font-bold text-lg mb-2">Schnelle Antwort garantiert!</h4>
              <p className="text-blue-100">
                Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white/10 backdrop-blur-md rounded-2xl shadow-xl p-8 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              Nachricht senden
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  Name *
                </label>
                <motion.input
                  whileFocus={{ scale: 1.02 }}
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all text-white placeholder-white/60"
                  placeholder="Ihr Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  E-Mail *
                </label>
                <motion.input
                  whileFocus={{ scale: 1.02 }}
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all text-white placeholder-white/60"
                  placeholder="ihre@email.de"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Nachricht *
                </label>
                <motion.textarea
                  whileFocus={{ scale: 1.02 }}
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all resize-none text-white placeholder-white/60"
                  placeholder="Beschreiben Sie Ihr Projekt..."
                />
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2 backdrop-blur-sm"
              >
                <SendIcon className="w-5 h-5" />
                <span>Nachricht senden</span>
              </motion.button>
            </form>

            <p className="text-xs text-blue-200 mt-4 text-center">
              * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;